import axios from "axios";
import { ElNotification } from "element-plus";
import {
  logout,
  getAccessToken,
  getRefreshToken,
  oktaAuth,
  unAuthorized,
} from "./authentification";

import {
  ENDPOINTS,
  MAINTENANCE_LABEL,
  UNAUTHORIZED,
  VARIABLE_NOT_FOUND,
} from "@/constants";
import { translate } from "@/i18n";
import { AUTHORIZE_URL, CLIENT_ID } from "@/okta_config";
import router from "@/router";

// Init httpClient
const api = axios.create();

const shownMessages: { [key: string]: boolean } = {
  post: false,
  put: false,
  delete: false,
  error: false,
};

const RESET_DELAY = 3000;

function resetShownMessages() {
  setTimeout(() => {
    Object.keys(shownMessages).forEach((key) => {
      shownMessages[key] = false;
    });
  }, RESET_DELAY);
}

// Setup Request Interceptor
api.interceptors.request.use(
  (response) => {
    const accessToken = getAccessToken();
    if (accessToken) {
      response.headers.Authorization = `Bearer ${accessToken}`;
    }
    return response;
  },
  (error) => {
    return Promise.reject(error.response);
  },
);

// Setup Response Interceptor
api.interceptors.response.use(
  (response) => {
    const method = response.config.method;
    const url = response.config.url as string;

    if (method && url.indexOf(ENDPOINTS.CHECK_FORMULA) === -1) {
      if (!shownMessages[method]) {
        switch (method) {
          case "post":
            ElNotification({
              title: translate("messages.Sucess"),
              dangerouslyUseHTMLString: true,
              message: `<strong>${translate(
                "messages.AddSucessMessage",
              )}</strong>`,
              type: "success",
            });
            break;
          case "put":
            ElNotification({
              title: translate("messages.Sucess"),
              dangerouslyUseHTMLString: true,
              message: `<strong>${translate(
                "messages.EditSucessMessage",
              )}</strong>`,
              type: "success",
            });
            break;
          case "delete":
            ElNotification({
              title: translate("messages.Sucess"),
              dangerouslyUseHTMLString: true,
              message: `<strong>${translate(
                "messages.DeleteSucessMessage",
              )}</strong>`,
              type: "success",
            });
            break;
        }
        shownMessages[method] = true;
        resetShownMessages();
      }
    }

    return response.data &&
      response.config.url?.indexOf("http://localhost:3000") === -1
      ? response.data
      : response;
  },
  async (error) => {
    const messages = error.response.data.message;

    console.error(messages);
    console.error(error.response.status);

    // Show error message only if it hasn't been shown
    if (!shownMessages["error"]) {
      if (messages.includes(MAINTENANCE_LABEL)) {
        router.push({
          name: "Maintenance",
        });
      } else if (messages.includes(VARIABLE_NOT_FOUND)) {
        console.log("Vars not found");
      } else if (messages.includes(UNAUTHORIZED)) {
        console.log("Unauthorized");
      } else {
        ElNotification({
          title: "Erreur",
          dangerouslyUseHTMLString: true,
          message: getTranslatedErrorMessage(messages[0]),
          type: "error",
        });
      }
      shownMessages["error"] = true;
      resetShownMessages();
    }

    // Handle token refresh
    if (unAuthorized === error.response.status) {
      const params = new URLSearchParams({
        grant_type: "refresh_token",
        redirect_uri: window.location.origin,
        scope: "offline_access openid",
        refresh_token: `${getRefreshToken()}`,
        client_id: `${CLIENT_ID}`,
      });

      try {
        const response = await getRefreshTokenCall(params);
        const data = response.data;
        oktaAuth.token.getWithoutPrompt().then((tokensBody: any) => {
          oktaAuth.tokenManager.add("accessToken", {
            ...tokensBody.tokens.access_token,
            accessToken: data.access_token,
          });
          oktaAuth.tokenManager.add("idToken", {
            ...tokensBody.tokens.idToken,
            accessToken: data.idToken,
          });
        });
      } catch (error) {
        console.error((error as any).response.data.error);
        logout();
      }

      window.location.reload();
    }
  },
);

async function getRefreshTokenCall(params: any) {
  return axios.post(AUTHORIZE_URL, params, {
    headers: {
      "content-type": "application/x-www-form-urlencoded",
    },
  });
}

function getTranslatedErrorMessage(message: string) {
  try {
    return translate(message);
  } catch (error) {
    return message;
  }
}

export { type AxiosResponse, type AxiosError } from "axios";
export const httpClient = api;
